const EMBEDDED_BROWSER_MARKERS = [
  'fbav',
  'fban',
  'instagram',
  'line/',
  'micromessenger',
];

const ANDROID_WEBVIEW_PATTERN = /;\s?wv\)?/;

const LAIS_APP_PATTERNS = [
  {
    app: 'agents',
    patterns: [/\bagents[\s/_-]?app\b/, /\blais[\s/_-]?agents\b/],
  },
  {
    app: 'tenants',
    patterns: [/\btenants[\s/_-]?app\b/, /\blais[\s/_-]?tenants\b/],
  },
  {
    app: 'corretores',
    patterns: [
      /\bcorretores\b/,
      /\bcorretores[\s/_-]?app\b/,
      /\bcorretor[\s/_-]?app\b/,
      /\blais[\s/_-]?corretores\b/,
    ],
  },
];

const INSTALL_PROMPT_EVENT_KEYS = [
  'appInstallPromptEvent',
  'beforeInstallPromptEvent',
  'deferredPrompt',
  'laisInstallPromptEvent',
];

const toLowerString = value => String(value || '').toLowerCase();

const safeNavigator = sourceWindow => {
  if (sourceWindow?.navigator) return sourceWindow.navigator;
  if (typeof navigator === 'undefined') return {};
  return navigator;
};

const safeWindow = sourceWindow =>
  sourceWindow || (typeof window === 'undefined' ? null : window);

const matchesStandaloneDisplay = sourceWindow => {
  const currentWindow = safeWindow(sourceWindow);
  if (!currentWindow?.matchMedia) return false;

  return Boolean(
    currentWindow.matchMedia('(display-mode: standalone)').matches
  );
};

const detectLaisAppClient = userAgent => {
  const matchedApp = LAIS_APP_PATTERNS.find(({ patterns }) =>
    patterns.some(pattern => pattern.test(userAgent))
  );

  return matchedApp?.app || 'none';
};

const hasInstallPromptEvent = currentWindow =>
  INSTALL_PROMPT_EVENT_KEYS.some(
    key => typeof currentWindow?.[key]?.prompt === 'function'
  );

const resolveInstallPromptState = ({
  currentNavigator,
  currentWindow,
  isKnownLaisApp,
  isStandalone,
  isWebView,
}) => {
  if (isKnownLaisApp) {
    return { eligible: false, reason: 'native_app' };
  }

  if (isWebView) {
    return { eligible: false, reason: 'webview' };
  }

  if (isStandalone) {
    return { eligible: false, reason: 'standalone' };
  }

  if (!('serviceWorker' in currentNavigator)) {
    return { eligible: false, reason: 'missing_service_worker' };
  }

  if (!hasInstallPromptEvent(currentWindow)) {
    return { eligible: false, reason: 'missing_install_prompt_event' };
  }

  return { eligible: true, reason: 'eligible' };
};

export const detectPlatform = sourceWindow => {
  const currentWindow = safeWindow(sourceWindow);
  const currentNavigator = safeNavigator(sourceWindow);
  const userAgent = toLowerString(currentNavigator.userAgent);
  const osPlatform = toLowerString(
    currentNavigator.userAgentData?.platform || currentNavigator.platform || ''
  );

  const isAndroid = userAgent.includes('android');
  const isIOS =
    /iphone|ipad|ipod/.test(userAgent) || /iphone|ipad|ipod/.test(osPlatform);
  const isMobile = isAndroid || isIOS || userAgent.includes('mobile');
  const appClient = detectLaisAppClient(userAgent);
  const isKnownLaisApp = appClient !== 'none';
  const hasAndroidWebViewMarker =
    isAndroid && ANDROID_WEBVIEW_PATTERN.test(userAgent);
  const hasIOSWebViewMarker =
    isIOS && userAgent.includes('mobile/') && !userAgent.includes('safari');
  const hasEmbeddedBrowserMarker = EMBEDDED_BROWSER_MARKERS.some(marker =>
    userAgent.includes(marker)
  );
  const isWebView = Boolean(
    isKnownLaisApp ||
      hasAndroidWebViewMarker ||
      hasIOSWebViewMarker ||
      hasEmbeddedBrowserMarker
  );
  const isStandalone = Boolean(
    currentNavigator.standalone || matchesStandaloneDisplay(sourceWindow)
  );
  const installPrompt = resolveInstallPromptState({
    currentNavigator,
    currentWindow,
    isKnownLaisApp,
    isStandalone,
    isWebView,
  });
  let clientPlatform = 'web';
  if (isIOS) {
    clientPlatform = 'ios';
  } else if (isAndroid) {
    clientPlatform = 'android';
  }
  let clientKind = 'browser';
  if (isKnownLaisApp) {
    clientKind = `${appClient}_app`;
  } else if (isWebView) {
    clientKind = 'webview';
  } else if (isStandalone) {
    clientKind = 'standalone';
  }

  return {
    platform: clientPlatform,
    deviceType: isMobile ? 'mobile' : 'desktop',
    clientKind,
    appClient,
    isMobile,
    isBrowser: clientKind === 'browser',
    isWebView,
    isStandalone,
    isAgentsApp: appClient === 'agents',
    isTenantsApp: appClient === 'tenants',
    isCorretoresApp: appClient === 'corretores',
    isKnownLaisApp,
    installPromptEligible: installPrompt.eligible,
    shouldShowAppInstallPrompt: installPrompt.eligible,
    installPromptReason: installPrompt.reason,
  };
};

export const platformRuntimeAttributes = platformState => {
  const state = platformState || detectPlatform();

  return {
    'data-lais-client-platform': state.platform,
    'data-lais-client-device': state.deviceType,
    'data-lais-client-kind': state.clientKind,
    'data-lais-app-client': state.appClient,
    'data-lais-is-webview': String(state.isWebView),
    'data-lais-install-prompt': state.shouldShowAppInstallPrompt
      ? 'eligible'
      : 'hidden',
  };
};

export const platformTrackingProperties = sourceWindow => {
  if (typeof window === 'undefined' && !sourceWindow) return {};
  const platform = detectPlatform(sourceWindow || window);

  return {
    clientPlatform: platform.platform,
    clientDeviceType: platform.deviceType,
    clientIsWebView: platform.isWebView,
    clientIsStandalone: platform.isStandalone,
  };
};
